import { TranslateService } from '@ngx-translate/core';
import { CommonService } from './common.service';
import {
    HttpClient,
    HttpEvent,
    HttpEventType,
    HttpHeaders,
    HttpProgressEvent,
    HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
    AggregateService,
    PlatformDataService,
    ShellDomainProvider,
    StorageDataService,
    UtilityService
} from '../standalone-packages/ecap3/core';
import { FileSaverService } from 'ngx-filesaver';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';
import { delay, map, retry, scan, switchMap } from 'rxjs/operators';
import { CustomSnackbarConfig, SCConfig } from '../../sc-config/constant/sc-config.constant';
import { Download, IFileResponse } from '../models';
import { UrlResolverService } from './url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class FileService {
    private readonly urlResolver = inject(UrlResolverService);
    conversionPipelineSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});
    getPreSignedUrlForUploadUrl = ShellDomainProvider.api.Storage + 'StorageQuery/GetPreSignedUrlForUpload';
    storageServiceCommandUrl = ShellDomainProvider.api.Storage + 'StorageCommand/';
    snackbarConfig: CustomSnackbarConfig = null;
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });

    constructor(
        private platformDataService: PlatformDataService,
        private aggregateService: AggregateService,
        private storageDataService: StorageDataService,
        private utilityService: UtilityService,
        private http: HttpClient,
        private fileSaverService: FileSaverService,
        private commonService: CommonService,
        private translateService: TranslateService
    ) {}

    resourceUpload(imageFiles, fileId, tag?, type?) {
        if (imageFiles && imageFiles.length) {
            const fileData = imageFiles[imageFiles.length - 1];
            return this.storageDataService
                .getPreSignedUrl(fileId, fileData.name, null, tag || [SCConfig.Tags.File], type || null)
                .pipe(
                    switchMap((response: any) => {
                        let modifiedUrl = '';
                        if (response && response.body) {
                            modifiedUrl = response.body.UploadUrl;
                            if (ShellDomainProvider['api'].IsStaging) {
                                modifiedUrl = response.body.UploadUrl.replace('http://', 'https://');
                            }
                        }
                        fileData['modifiedUrl'] = modifiedUrl;

                        if (type === 'Public') {
                            return this.storageDataService
                                .uploadPublicPresignedFile(modifiedUrl, fileData)
                                .toPromise()
                                .then(
                                    presignedResponse => {
                                        return response.body;
                                    },
                                    error => {
                                        console.error('Error: File uploading to cloud', error);
                                        return error;
                                    }
                                );
                        }
                        return this.storageDataService
                            .uploadPresignedFile(modifiedUrl, fileData)
                            .toPromise()
                            .then(
                                presignedResponse => {
                                    return response.body;
                                },
                                error => {
                                    console.error('Error: File uploading to cloud', error);
                                    return error;
                                }
                            );
                    })
                );
        }
        return null;
    }

    getFilePresignedUrl(fileId, fileName): Observable<any> {
        return this.storageDataService.getPreSignedUrl(fileId, fileName, null, [SCConfig.Tags.File], null).pipe(
            map((response: any) => {
                let modifiedUrl = '';
                if (response && response.body) {
                    modifiedUrl = response.body.UploadUrl;
                    if (ShellDomainProvider['api'].IsStaging) {
                        modifiedUrl = response.body.UploadUrl.replace('http://', 'https://');
                    }
                }
                return modifiedUrl;
            })
        );
    }

    getFileById(fileId): Observable<IFileResponse> {
        return this.storageDataService.getFileInfo(fileId).pipe(
            map(
                (response: any) => {
                    const data = response.body;
                    if (data && data.Url) {
                        return data;
                    }
                },
                error => {
                    console.error('Error GetFileInfo', error);
                    return null;
                }
            )
        );
    }

    getFilesByIds(fileIds) {
        const fileList = [];
        return this.storageDataService.getFilesInfo(fileIds).pipe(
            map((response: Response) => {
                if (!response || !response.body) return [];
                const file: any = response.body;
                for (let i = 0; i < file.length; i++) {
                    fileList.push({
                        fileObj: file[i],
                        fileId: file[i].ItemId
                    });
                }
                return fileList;
            })
        );
    }

    deleteSingleFile(fileId): Observable<any> {
        return this.storageDataService.deleteFile(fileId);
    }

    deleteAllFiles(filesIds): Observable<any> {
        return this.storageDataService.deleteAllFiles(filesIds);
    }

    getFileConnectionWithConversionAggregatePayloads(
        fileId,
        tagName,
        parentEntityName,
        parentEntityItemId,
        dimensions?
    ) {
        const connectionId = this.utilityService.getNewGuid();
        const connection = {
            ItemId: connectionId,
            ParentEntityName: parentEntityName,
            ParentEntityID: parentEntityItemId,
            ChildEntityName: SCConfig.Entity.File,
            ChildEntityID: fileId,
            Tags: [tagName]
        };

        const callList = [];
        const connectionPayload = this.aggregateService.aggregateConnectModel(connection);
        callList.push(connectionPayload);

        return callList;
    }

    getResourceImage(entityName, entityItemId, tag): Observable<any> {
        return this.platformDataService.getResource(entityName, entityItemId, tag).pipe(
            map((response: any) => {
                const data = response.body;
                if (data && data.Result !== '') {
                    return data.Result;
                }
                return null;
            })
        );
    }

    getImageConnectionWithConversionAggregatePayloads(
        fileId,
        tagName,
        parentEntityName,
        parentEntityItemId,
        taskId?,
        dimensions?
    ) {
        const connectionId = this.utilityService.getNewGuid();
        const connection = {
            ItemId: connectionId,
            ParentEntityName: parentEntityName,
            ParentEntityID: parentEntityItemId,
            ChildEntityName: SCConfig.Entity.File,
            ChildEntityID: fileId,
            Tags: [tagName]
        };

        const callList = [];
        const connectionPayload = this.aggregateService.aggregateConnectModel(connection);
        callList.push(connectionPayload);

        // Create image conversions connections
        taskId = taskId || this.utilityService.getNewGuid();
        dimensions = dimensions || SCConfig.ThumbnailDimensions;

        const parentEntities = [
            {
                Id: parentEntityItemId,
                Name: parentEntityName,
                TagPrefix: tagName
            }
        ];
        const executeFileConversionPipelinePayload = this.fileConversionPipelineAggregatePayload(
            fileId,
            taskId,
            parentEntities,
            dimensions
        );
        callList.push(executeFileConversionPipelinePayload);
        return callList;
    }

    fileConversionPipelineAggregatePayload(sourceFileId, taskId, parentEntities, dimensions?) {
        const requestProperties = {
            SourceFileId: sourceFileId,
            Dimensions: SCConfig.ThumbnailDimensions,
            ParentEntities: []
        };

        if (dimensions) {
            requestProperties.Dimensions = dimensions;
        }

        if (parentEntities) {
            requestProperties.ParentEntities = parentEntities;
        }

        const commandPayload = {
            ConversionPipelineId: 'ECAP-IRPL-v.1',
            TaskId: taskId,
            RequestProperties: {
                ImageResizeSetting: JSON.stringify(requestProperties)
            }
        };

        return this.aggregateService.aggregateExecuteFileConversionPipelineUrlModel(commandPayload);
    }

    createFileConnectionWithConversion(fileId, tagName, parentEntityName, parentEntityItemId, dimensions?) {
        const connectionId = this.utilityService.getNewGuid();
        const connection = {
            ItemId: connectionId,
            ParentEntityName: parentEntityName,
            ParentEntityID: parentEntityItemId,
            ChildEntityName: SCConfig.Entity.File,
            ChildEntityID: fileId,
            Tags: [tagName]
        };

        return this.platformDataService.connect(connection).pipe(
            map((response: any) => {
                const data = response.body;
                if (data.StatusCode === 0) {
                    const taskId = this.utilityService.getNewGuid();
                    const parentEntities = [
                        {
                            Id: parentEntityItemId,
                            Name: parentEntityName,
                            TagPrefix: tagName
                        }
                    ];
                    return this.executePipeline(fileId, taskId, parentEntities, dimensions);
                }
                return response;
            })
        );
    }

    executePipeline(sourceFileId, taskId, parentEntities, dimensions?) {
        const requestProperties = {
            SourceFileId: sourceFileId,
            Dimensions: [
                {
                    Width: 32,
                    Height: 32
                },
                {
                    Width: 128,
                    Height: 128
                },
                {
                    Width: 64,
                    Height: 64
                },
                {
                    Width: 2100,
                    Height: 2100
                }
            ],
            ParentEntities: []
        };

        if (dimensions) {
            requestProperties.Dimensions = dimensions;
        }

        if (parentEntities) {
            requestProperties.ParentEntities = parentEntities;
        }

        const commandPayload = {
            ConversionPipelineId: 'ECAP-IRPL-v.1',
            TaskId: taskId,
            RequestProperties: {
                ImageResizeSetting: JSON.stringify(requestProperties)
            }
        };

        return this.storageDataService.executePipeline(commandPayload).pipe(
            map(response => {
                return response;
            })
        );
    }

    downloadFile(fileDataUrl, fileName) {
        window.location.href = fileDataUrl;
    }

    downloadFileByFileId(fileId: string) {
        this.getFileById(fileId).subscribe(file => this.createUrl(file));
    }

    createUrl(file): void {
        this.http.get(file.Url, { responseType: 'blob' }).subscribe(blob => {
            const fileName = encodeURI(file.Name);
            this.fileSaverService.save(blob, fileName);
        });
    }

    completeObservable(requestObservable: Subscriber<any>, value) {
        requestObservable.next(value);
        requestObservable.complete();
    }

    downloadFileByFileIdWithObservable(fileId: string) {
        return new Observable(requestObservable => {
            this.getFileById(fileId).subscribe(
                file => {
                    this.createUrlWithObservable(file, requestObservable);
                },
                error => {}
            );
        });
    }

    createUrlWithObservable(file, requestObservable) {
        const url = file.Url;
        this.http.get(url, { responseType: 'blob' }).subscribe(
            blob => {
                const fileName = encodeURI(file.Name);
                this.fileSaverService.save(blob, fileName);
                this.completeObservable(requestObservable, true);
            },
            error => {
                if (!file?.Url.includes('.zip')) {
                    this.showFileDownloadError(file && file.Name);
                }
                this.completeObservable(requestObservable, false);
            }
        );
    }

    downloadFileByFileIdWithObservableWithRetry(
        fileId: string,
        currentRetryCount: number = 0,
        maxRetryCount: number = 5
    ) {
        return new Observable(requestObservable => {
            this.getFileById(fileId).subscribe(
                file => {
                    this.createUrlWithObservableWithRetry(file, requestObservable, currentRetryCount, maxRetryCount);
                },
                error => {
                    console.error('downloadFileByFileIdWithObservableWithRetry ', error);
                }
            );
        });
    }
    createUrlWithObservableWithRetry(
        file: IFileResponse,
        requestObservable,
        currentRetryCount: number = 0,
        maxRetryCount: number = 5
    ) {
        let url = file.Url;
        this.http.get(url, { responseType: 'blob' }).subscribe(
            blob => {
                const fileName = encodeURI(file.Name);
                this.fileSaverService.save(blob, fileName);
                this.completeObservable(requestObservable, true);
            },
            error => {
                console.error('createUrlWithObservableWithRetry', error);
                if (error?.status === 404 && currentRetryCount <= maxRetryCount) {
                    setTimeout(() => {
                        this.createUrlWithObservableWithRetry(
                            file,
                            requestObservable,
                            currentRetryCount + 1,
                            maxRetryCount
                        );
                    }, 3000);
                } else {
                    if (!file?.Url.includes('.zip')) {
                        this.showFileDownloadError(file && file.Name);
                    }
                    this.completeObservable(requestObservable, false);
                }
            }
        );
    }
    createUrlWithObservableReturn(file: IFileResponse) {
        return new Observable(requestObservable => {
            this.http.get(file.Url, { responseType: 'blob' }).subscribe(
                blob => {
                    const fileName = encodeURI(file.Name);
                    this.fileSaverService.save(blob, fileName);
                    this.completeObservable(requestObservable, true);
                },
                error => {
                    this.showFileDownloadError(file && file.Name);
                    this.completeObservable(requestObservable, false);
                }
            );
        });
    }

    createUrlWithObservableReturnWithRetry(file: IFileResponse, maxRetryCount: number = 5) {
        let url = file.Url;
        return new Observable(requestObservable => {
            this.http
                .get(url, { responseType: 'blob' })
                .pipe(
                    retry({
                        count: maxRetryCount,
                        delay: 3000
                    })
                )
                .subscribe(
                    blob => {
                        const fileName = encodeURI(file.Name);
                        this.fileSaverService.save(blob, fileName);
                        this.completeObservable(requestObservable, true);
                    },
                    error => {
                        this.showFileDownloadError(file && file.Name);
                        this.completeObservable(requestObservable, false);
                    }
                );
        });
    }

    downloadFileByFileIdWithResponse(fileId: string, object?, key?, keyDelete?) {
        this.getFileById(fileId).subscribe(
            file => {
                this.createUrlWithResponse(file, object, key, keyDelete);
            },
            error => {
                this.commonService.openSnackBar(this.translateService.instant('SOMETHING_WENT_WRONG'));
                object[key] = false;
                if (keyDelete) {
                    delete object[key];
                }
            }
        );
    }

    createUrlWithResponse(file, object?, key?, keyDelete?) {
        this.http.get(file.Url, { responseType: 'blob' }).subscribe(
            blob => {
                const fileName = encodeURI(file.Name);
                this.fileSaverService.save(blob, fileName);
                object[key] = false;
                if (keyDelete) {
                    delete object[key];
                }
            },
            error => {
                object[key] = false;
                if (keyDelete) {
                    delete object[key];
                }
                this.showFileDownloadError(file && file.Name);
            }
        );
    }

    /*downloadFileWithProgress(fileId): Observable<Download> {
        return new Observable<Download>(obs => {
            this.getFileById(fileId).pipe(map((file => 
            this.http.get(file.Url, {
                reportProgress: true,
                observe: 'events',
                responseType: 'blob'
            }).pipe(this.download(blob => saveAs(blob, filename)))

        }))
    }*/

    download(saver?: (b: Blob) => void): (source: Observable<HttpEvent<Blob>>) => Observable<Download> {
        return (source: Observable<HttpEvent<Blob>>) =>
            source.pipe(
                scan(
                    (previous: Download, event: HttpEvent<Blob>): Download => {
                        if (this.isHttpProgressEvent(event)) {
                            return {
                                progress: event.total
                                    ? Math.round((100 * event.loaded) / event.total)
                                    : previous.progress,
                                state: 'IN_PROGRESS',
                                content: null
                            };
                        }
                        if (this.isHttpResponse(event)) {
                            if (saver && event.body) {
                                saver(event.body);
                            }
                            return {
                                progress: 100,
                                state: 'DONE',
                                content: event.body
                            };
                        }
                        return previous;
                    },
                    { state: 'PENDING', progress: 0, content: null }
                )
            );
    }

    isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
        return event.type === HttpEventType.Response;
    }

    isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
        return event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress;
    }

    singleFileUpload(file, fileId) {
        const Name = file.name;
        this.storageDataService.getPreSignedUrl(fileId, Name, '', ['file']).subscribe(response3 => {
            const url = response3.body.UploadUrl;
            this.storageDataService.uploadPresignedFile(url, file).subscribe(response4 => {});
        });
    }
    singleFileUploadWithObs(file, fileId) {
        const Name = file.name;
        return new Observable<any>(obs => {
            this.storageDataService.getPreSignedUrl(fileId, Name, '', ['file']).subscribe(response3 => {
                const url = response3.body.UploadUrl;
                this.storageDataService.uploadPresignedFile(url, file).subscribe(response4 => {
                    obs.next(response4);
                    obs.complete();
                });
            });
        });
    }

    singleFileUploadWithProgress(file: File, itemId: string, tags?: string[], metadata?: object): Observable<number> {
        if (tags && !tags.includes('File')) {
            tags.push('File');
        } else {
            tags = ['File'];
        }
        if (!metadata) {
            metadata = {
                FileInfo: {
                    Value: file.name,
                    Type: file.type
                }
            };
        }
        const payload = {
            ItemId: itemId,
            Name: file.name,
            Tags: JSON.stringify(tags),
            MetaData: JSON.stringify(metadata),
            ParentDirectoryId: ''
        };
        return new Observable<number>(obs => {
            this.http.post(this.getPreSignedUrlForUploadUrl, payload, SCConfig.HttpCommonOptions).subscribe(
                (response: any) => {
                    this.storageDataService
                        .uploadPresignedFileWithProgress(payload, response.UploadUrl, file)
                        .subscribe(res => {
                            obs.next(res.uploadPercentage);
                            if (res.uploadPercentage === 100) {
                                obs.complete();
                            }
                        });
                },
                error => {
                    obs.error(error);
                }
            );
        });
    }
    showFileDownloadError(fileName) {
        let message =
            this.translateService.instant('ERROR_DOWNLOADING_FILE') +
            ' ' +
            fileName +
            '.' +
            this.translateService.instant('TRY_AGAIN');
        this.snackbarConfig = new CustomSnackbarConfig();
        this.snackbarConfig.Type = SCConfig.SnackbarType.ERROR;
        this.snackbarConfig.Message = message;
        this.commonService.showCustomSnackbarMessage(this.snackbarConfig);
    }
    getFileStatusForInsuranceCase(fileIds: string[], insuranceCaseId: string) {
        const caseFilterStringUrl = `${this.urlResolver.getPcxQueryUrl()}GetFileStatus`;
        const payload = {
            FileIds: fileIds,
            InsuranceCaseId: insuranceCaseId
        };
        return this.http
            .post(caseFilterStringUrl, payload, { headers: this.header, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response['body'];
                })
            );
    }

    downloadZipFile(payload: any): Observable<any> {
        const url: string = this.storageServiceCommandUrl + 'CompressFile';
        return this.http.post(url, payload, { headers: this.header, withCredentials: true, observe: 'response' }).pipe(
            map((response: any) => {
                return response['body'];
            })
        );
    }

    // USED BY IPEX FILE SERVICE
    public getFileAsBlob(fileMetadata?: IFileResponse): Observable<Blob> {
        // Use provided metadata directly
        if (!fileMetadata || !fileMetadata.Url) {
            throw new Error('File not found or invalid file data');
        }
        return this.http.get(fileMetadata.Url, { responseType: 'blob' });
    }
}
